import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/** ******************************************************************************
 * Copyright (c) 2022 Precies. Software and others
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License v. 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 * ****************************************************************************** */
import { useContext, useEffect, useRef, useState } from 'react';
import { Button, Dialog, DialogTitle, DialogContent, Box, TextField, DialogActions } from '@mui/material';
import { ButtonWithProgress } from '../../../components/button-with-progress';
import { MainContext } from '../../../context';
import { useCreateNamespace } from './use-user-namespaces';
const NAMESPACE_NAME_SIZE = 255;
export const CreateNamespaceDialog = props => {
    const [name, setName] = useState('');
    const [nameError, setNameError] = useState();
    const context = useContext(MainContext);
    const { mutateAsync: createNamespace, isPending: creating } = useCreateNamespace();
    useEffect(() => {
        if (props.open) {
            setName('');
            setNameError(undefined);
        }
    }, [props.open]);
    useEffect(() => {
        document.addEventListener('keydown', handleEnter);
        return () => {
            document.removeEventListener('keydown', handleEnter);
        };
    });
    const handleNameChange = (event) => {
        const name = event.target.value;
        let nameError;
        if (name.length > NAMESPACE_NAME_SIZE) {
            nameError = `The namespace name must not be longer than ${NAMESPACE_NAME_SIZE} characters.`;
        }
        setName(name);
        setNameError(nameError);
    };
    // The Enter shortcut reaches this straight from the document, so whatever disables the button
    // has to be checked here as well. Held keys repeat faster than a render, so the in-flight check
    // is a ref rather than the mutation's pending flag, which only turns the spinner on.
    const invalidName = Boolean(nameError) || !name;
    const submitting = useRef(false);
    const handleCreateNamespace = async () => {
        if (!context.user || invalidName || submitting.current) {
            return;
        }
        submitting.current = true;
        try {
            await createNamespace(name);
            props.onClose();
            props.namespaceCreated(name);
        }
        catch (err) {
            context.handleError(err);
        }
        finally {
            submitting.current = false;
        }
    };
    const handleEnter = (e) => {
        if (props.open && e.code === 'Enter') {
            handleCreateNamespace();
        }
    };
    return (_jsxs(Dialog, { open: props.open, onClose: props.onClose, children: [_jsx(DialogTitle, { children: "Create new namespace" }), _jsx(DialogContent, { children: _jsx(Box, { my: 2, children: _jsx(TextField, { fullWidth: true, label: 'Namespace Name', error: Boolean(nameError), helperText: nameError, onChange: handleNameChange }) }) }), _jsxs(DialogActions, { children: [_jsx(Button, { onClick: props.onClose, children: "Cancel" }), _jsx(ButtonWithProgress, { autoFocus: true, sx: { ml: 1 }, error: invalidName, working: creating, onClick: handleCreateNamespace, children: "Create Namespace" })] })] }));
};
//# sourceMappingURL=create-namespace-dialog.js.map